import React, { useEffect, useState } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, 
    Button, LinearProgress, Typography, Box 
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const SessionHandler = () => {
    const { showIdleModal, stayActive, logout, logoutGracePeriod } = useAuth();
    const [progress, setProgress] = useState(100);
    const [secondsLeft, setSecondsLeft] = useState(logoutGracePeriod / 1000);

    useEffect(() => {
        let interval;
        if (showIdleModal) {
            // Reset state when modal opens
            setSecondsLeft(logoutGracePeriod / 1000);
            setProgress(100);
            
            const startTime = Date.now();
            const endTime = startTime + logoutGracePeriod;

            // Flash Tab Title
            const originalTitle = document.title;
            let isFlash = false;

            interval = setInterval(() => {
                const now = Date.now();
                const remaining = Math.max(0, endTime - now);
                
                // Update Title
                document.title = isFlash ? "⚠️ Session Expiring!" : "Action Required";
                isFlash = !isFlash;

                // Update UI
                setSecondsLeft(Math.ceil(remaining / 1000));
                setProgress((remaining / logoutGracePeriod) * 100);

                if (remaining <= 0) {
                    clearInterval(interval);
                    document.title = originalTitle;
                }
            }, 500); // Update every half second for smoother feel

            return () => {
                clearInterval(interval);
                document.title = originalTitle; // Restore title on unmount/close
            };
        }
    }, [showIdleModal, logoutGracePeriod]);

    if (!showIdleModal) return null;

    return (
        <Dialog 
            open={showIdleModal} 
            onClose={() => {}} // User cannot close by clicking outside
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: { borderTop: '6px solid #ed6c02' } // Orange warning border
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon color="warning" /> Session Expiring
            </DialogTitle>
            <DialogContent>
                <DialogContentText gutterBottom>
                    You have been inactive for a while. To protect your data, you will be logged out automatically.
                </DialogContentText>
                
                <Box sx={{ mt: 3, mb: 1, textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight="bold" color="warning.main">
                        {secondsLeft}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        seconds remaining
                    </Typography>
                </Box>

                <LinearProgress variant="determinate" value={progress} color="warning" sx={{ height: 8, borderRadius: 4 }} />
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 2 }}>
                <Button onClick={() => logout('Logged out by user.')} color="inherit">
                    Logout Now
                </Button>
                <Button 
                    onClick={stayActive} 
                    variant="contained" 
                    color="primary"
                    size="large"
                    autoFocus
                >
                    I'm Still Here
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionHandler;